import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  PartyPopper, DoorOpen, UserPlus, ScrollText, Ticket, CalendarClock, Save, Send, Plus, Trash2, Zap, Mail, Gem, SmilePlus, Loader2, Lightbulb,
} from "lucide-react";
import { botGet, botPut, botPost, apiError } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChannelSelect, MultiCheck } from "@/components/pickers";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Field, HowItWorks, Loading, MessageMini, PageHeader, TextInput, cleanMessage, inputC, outlineBtn, primaryBtn } from "@/components/kit";

const DAYS = ["domenica", "lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato"];

function buildCron(f) {
  const [h, m] = (f.time || "09:00").split(":").map(Number);
  if (f.freq === "daily") return `${m} ${h} * * *`;
  if (f.freq === "weekly") return `${m} ${h} * * ${f.dow ?? 1}`;
  if (f.freq === "monthly") return `${m} ${h} ${f.dom ?? 1} * *`;
  if (f.freq === "interval") return `*/${f.every ?? 30} * * * *`;
  return "0 9 * * *";
}

// Traduce le espressioni create da questa pagina in italiano ("ogni lunedì alle 09:00").
function describeCron(cron) {
  const p = (cron || "").trim().split(/\s+/);
  if (p.length !== 5) return cron;
  const [m, h, dom, , dow] = p;
  const every = m.match(/^\*\/(\d+)$/);
  if (every && h === "*") return `ogni ${every[1]} minuti`;
  if (!/^\d+$/.test(m) || !/^\d+$/.test(h)) return cron;
  const at = `alle ${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
  if (dom === "*" && dow === "*") return `ogni giorno ${at}`;
  if (dom === "*" && /^\d$/.test(dow)) return `ogni ${DAYS[Number(dow) % 7]} ${at}`;
  if (/^\d+$/.test(dom) && dow === "*") return `il giorno ${dom} di ogni mese ${at}`;
  return cron;
}

const TONES = {
  blue: "from-[#5865F2] to-[#8B5CF6]", green: "from-[#10B981] to-[#22D3EE]", red: "from-[#EF4444] to-[#F97316]",
  pink: "from-[#EC4899] to-[#F47FFF]", amber: "from-[#F59E0B] to-[#F97316]", slate: "from-[#475569] to-[#64748B]", cyan: "from-[#0EA5E9] to-[#00F0FF]",
};

function FeatureCard({ icon: Icon, tone = "blue", title, desc, tip, enabled, onToggle, onSave, onTest, testLabel = "Invia prova", saving, testId, badge, children }) {
  return (
    <div className={`card-elevated overflow-hidden transition-colors ${enabled ? "ring-1 ring-[#5865F2]/25" : ""}`} data-testid={testId}>
      <div className="flex items-start gap-3 p-5">
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${TONES[tone]} shadow-[0_8px_20px_-10px_rgba(88,101,242,0.9)]`}><Icon className="h-5 w-5 text-white" /></div>
        <div className="min-w-0 flex-1">
          <p className="flex flex-wrap items-center gap-2 font-bold text-white">{title}{badge && <span className="rounded-full bg-[#00F0FF]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#00F0FF]">{badge}</span>}</p>
          <p className="text-sm text-[#949BA4]">{desc}</p>
        </div>
        <Switch checked={!!enabled} onCheckedChange={onToggle} data-testid={`${testId}-toggle`} />
      </div>
      {enabled && (
        <div className="space-y-4 border-t border-[#232838] bg-[#12141d]/60 p-5">
          {children}
          {tip && <p className="flex gap-2 rounded-xl bg-[#FEE75C]/[0.06] px-3 py-2 text-sm text-[#f5e6a0]"><Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-[#FEE75C]" /><span>{tip}</span></p>}
          <div className="flex flex-wrap gap-2">
            <Button onClick={onSave} disabled={saving} className={primaryBtn} data-testid={`${testId}-save`}>{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Salva</Button>
            {onTest && <Button variant="outline" onClick={onTest} className={outlineBtn}><Send className="mr-2 h-4 w-4" />{testLabel}</Button>}
          </div>
        </div>
      )}
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="px-1 text-xs font-bold uppercase tracking-widest text-[#6b7185]">{title}</h2>
    <div className="space-y-3">{children}</div>
  </section>
);

export default function Automations() {
  const { meta, roles } = useData();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(null);
  const [reactOpen, setReactOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelChannel, setPanelChannel] = useState("");
  const [schedFreq, setSchedFreq] = useState({ freq: "daily", time: "09:00", dow: 1, dom: 1, every: 30 });

  const load = async () => { try { setF(await botGet("features")); } catch (e) { toast.error(apiError(e)); } };
  useEffect(() => { load(); }, []);

  const upd = (name, patch) => setF((s) => ({ ...s, [name]: { ...s[name], ...patch } }));
  const clean = (name, v) => {
    if (["welcome", "goodbye", "boost", "welcomedm"].includes(name)) return { ...v, message: cleanMessage(v.message) };
    if (name === "tickets") return { ...v, openMessage: cleanMessage(v.openMessage) };
    if (name === "scheduled") return { ...v, items: v.items.filter((i) => i.channelId).map((i) => ({ ...i, message: cleanMessage(i.message) })) };
    if (name === "autoreact") return { items: v.items.filter((i) => i.channelId && i.emojis.length) };
    return v;
  };
  const save = async (name, value = f[name]) => {
    setSaving(name);
    try { await botPut(`features/${name}`, clean(name, value)); toast.success("Salvato", { description: "È già attivo sul server." }); return true; }
    catch (e) { toast.error(apiError(e)); return false; } finally { setSaving(null); }
  };
  // Accendere richiede spesso un canale: si accende solo nel pannello, si salva con «Salva».
  // Spegnere invece si applica subito.
  const toggle = async (name, v) => {
    upd(name, { enabled: v });
    if (!v) await save(name, { ...f[name], enabled: false });
  };
  const test = async (name) => {
    try { await save(name); await botPost(`features/${name}/test`, {}); toast.success(name === "welcomedm" ? "Prova inviata in privato al proprietario del server" : "Messaggio di prova inviato"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!f) return <Loading />;

  const roleOpts = roles.filter((r) => !r.isEveryone).map((r) => ({ value: r.id, label: r.name, color: r.color }));
  const placeholders = (meta?.placeholders || []).map((p) => p.value).join(" ");

  const sched = f.scheduled || { items: [] };
  const addSched = () => upd("scheduled", { items: [...(sched.items || []), { id: crypto.randomUUID(), enabled: true, channelId: null, cron: buildCron(schedFreq), message: { content: "" } }] });
  const updSched = (id, patch) => upd("scheduled", { items: sched.items.map((it) => (it.id === id ? { ...it, ...patch } : it)) });

  const react = f.autoreact || { items: [] };
  const updReact = (i, patch) => upd("autoreact", { items: react.items.map((it, j) => (j === i ? { ...it, ...patch } : it)) });

  const createPanel = async () => {
    if (!panelChannel) return toast.error("Seleziona un canale");
    try {
      await botPut("features/tickets", clean("tickets", f.tickets));
      await botPost(`channels/${panelChannel}/messages`, {
        embeds: [{ title: "🎫 Assistenza", description: "Hai bisogno dello staff? Premi il bottone qui sotto: si aprirà un canale privato solo per te.", color: "#5865F2" }],
        buttons: [{ label: "Apri ticket", emoji: "🎫", action: "ticket", style: "primary" }],
      });
      toast.success("Pannello ticket inviato"); setPanelOpen(false);
    } catch (e) { toast.error(apiError(e)); }
  };

  const card = (name) => ({ enabled: f[name]?.enabled, onToggle: (v) => toggle(name, v), onSave: () => save(name), saving: saving === name, testId: `automation-${name}` });

  return (
    <div className="space-y-8 pb-10">
      <PageHeader icon={Zap} title="Funzioni automatiche" subtitle="Cose che il bot fa da solo, 24 ore su 24, senza che tu debba intervenire." />

      <HowItWorks id="automations"
        intro="Ogni riquadro è una funzione indipendente. Accendi l'interruttore, compila i campi e premi Salva: la funzione parte subito, senza riavviare il bot."
        steps={[
          <>Accendi l'<b className="text-white">interruttore</b> della funzione che ti interessa.</>,
          <>Scegli il <b className="text-white">canale</b> e scrivi il messaggio: a destra vedi l'anteprima come su Discord.</>,
          <>Premi <b className="text-white">Salva</b> e usa <b className="text-white">Invia prova</b> per vedere il risultato vero nel server.</>,
        ]}
        tips={[
          <>Nei messaggi puoi usare i segnaposto, che il bot sostituisce: <span className="font-mono text-[#8ea0ff]">{placeholders || "{user} {server} {memberCount}"}</span>.</>,
          "Il ruolo del bot deve stare sopra i ruoli che assegna (Ruoli → trascinalo in alto), altrimenti Discord glielo impedisce.",
        ]} />

      <Section title="Quando entra o esce qualcuno">
        <FeatureCard icon={PartyPopper} tone="blue" title="Benvenuto" desc="Messaggio pubblico quando entra un nuovo membro." onTest={() => test("welcome")}
          tip="Un embed con {user.avatar} come miniatura rende il benvenuto molto più curato. Citare il canale delle regole aiuta i nuovi a orientarsi." {...card("welcome")}>
          <Field label="Canale"><ChannelSelect value={f.welcome.channelId} onChange={(v) => upd("welcome", { channelId: v })} /></Field>
          <MessageMini value={f.welcome.message} onChange={(message) => upd("welcome", { message })} hint={placeholders && `Segnaposto: ${placeholders}`} />
        </FeatureCard>

        <FeatureCard icon={Mail} tone="cyan" title="Benvenuto in privato" badge="Nuovo" desc="Il bot scrive in DM a chi entra (se ha i messaggi privati aperti)." onTest={() => test("welcomedm")} testLabel="Prova su di me"
          tip="Ottimo per le informazioni che non vuoi intasare la chat: link utili, come verificarsi, come aprire un ticket." {...card("welcomedm")}>
          <MessageMini value={f.welcomedm?.message} onChange={(message) => upd("welcomedm", { message })} hint={placeholders && `Segnaposto: ${placeholders}`} />
        </FeatureCard>

        <FeatureCard icon={UserPlus} tone="green" title="Ruoli automatici" desc="Ruoli dati subito a chi entra (separati per persone e bot)."
          tip="Dai a tutti un ruolo base «Membro» e togli i permessi a @everyone: così controlli tutto da un solo ruolo." {...card("autorole")}>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Ruoli per le persone"><MultiCheck options={roleOpts} value={f.autorole.roleIds || []} onChange={(v) => upd("autorole", { roleIds: v })} /></Field>
            <Field label="Ruoli per i bot"><MultiCheck options={roleOpts} value={f.autorole.botRoleIds || []} onChange={(v) => upd("autorole", { botRoleIds: v })} /></Field>
          </div>
        </FeatureCard>

        <FeatureCard icon={DoorOpen} tone="slate" title="Addio" desc="Messaggio quando un membro lascia il server." onTest={() => test("goodbye")}
          tip="Mettilo in un canale dello staff invece che in quello pubblico, se non vuoi che tutti vedano chi esce." {...card("goodbye")}>
          <Field label="Canale"><ChannelSelect value={f.goodbye.channelId} onChange={(v) => upd("goodbye", { channelId: v })} /></Field>
          <MessageMini value={f.goodbye.message} onChange={(message) => upd("goodbye", { message })} hint={placeholders && `Segnaposto: ${placeholders}`} />
        </FeatureCard>
      </Section>

      <Section title="Community">
        <FeatureCard icon={Gem} tone="pink" title="Ringraziamento boost" badge="Nuovo" desc="Un messaggio di ringraziamento quando qualcuno potenzia il server." onTest={() => test("boost")}
          tip="Usa {boostCount} per mostrare quanti boost ha il server. Abbinalo a un ruolo «Booster» con un colore speciale." {...card("boost")}>
          <Field label="Canale"><ChannelSelect value={f.boost?.channelId} onChange={(v) => upd("boost", { channelId: v })} /></Field>
          <MessageMini value={f.boost?.message} onChange={(message) => upd("boost", { message })} hint={`Segnaposto: ${placeholders} {boostCount}`} />
        </FeatureCard>

        <FeatureCard icon={SmilePlus} tone="amber" title="Reazioni automatiche" badge="Nuovo" desc="Il bot aggiunge da solo delle reazioni a ogni messaggio dei canali scelti."
          enabled={react.items.length > 0 || reactOpen} onToggle={(v) => { setReactOpen(v); if (!v) save("autoreact", { items: [] }).then((ok) => ok && upd("autoreact", { items: [] })); }}
          onSave={() => save("autoreact")} saving={saving === "autoreact"} testId="automation-autoreact"
          tip="Idee: 👋 in #presentazioni, ✅ ❌ in #proposte per votare, ❤️ in #foto. Puoi usare anche emoji personalizzate del server scrivendo <:nome:id>.">
          {react.items.map((it, i) => (
            <div key={i} className="grid items-end gap-3 rounded-xl border border-[#232838] bg-[#131620] p-3 md:grid-cols-[1fr_1fr_auto_auto]">
              <Field label="Canale"><ChannelSelect value={it.channelId} onChange={(channelId) => updReact(i, { channelId })} types={["text", "announcement", "forum"]} /></Field>
              <Field label="Emoji (separate da spazio)"><TextInput value={it.emojis.join(" ")} onChange={(t) => updReact(i, { emojis: t.split(/\s+/).filter(Boolean).slice(0, 10) })} placeholder="👋 ❤️" /></Field>
              <label className="flex h-9 items-center gap-2 text-sm text-[#B5BAC1]"><Switch checked={it.ignoreBots} onCheckedChange={(ignoreBots) => updReact(i, { ignoreBots })} />Ignora i bot</label>
              <Button size="icon" variant="ghost" onClick={() => upd("autoreact", { items: react.items.filter((_, j) => j !== i) })} className="text-[#F23F43] hover:bg-[#F23F43]/10"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button variant="outline" onClick={() => upd("autoreact", { items: [...react.items, { channelId: null, emojis: ["👍"], ignoreBots: true }] })} className={outlineBtn} data-testid="autoreact-add"><Plus className="mr-2 h-4 w-4" />Aggiungi canale</Button>
        </FeatureCard>
      </Section>

      <Section title="Staff e sicurezza">
        <FeatureCard icon={ScrollText} tone="slate" title="Registro (log)" desc="Il bot annota in un canale privato cosa succede nel server."
          tip="Crea un canale #log visibile solo allo staff. Se ti arrivano troppi messaggi, togli «messaggio modificato»." {...card("logs")}>
          <Field label="Canale dei log"><ChannelSelect value={f.logs.channelId} onChange={(v) => upd("logs", { channelId: v })} /></Field>
          <Field label="Cosa registrare"><MultiCheck options={meta?.logEvents || []} value={f.logs.events || []} onChange={(v) => upd("logs", { events: v })} /></Field>
        </FeatureCard>

        <FeatureCard icon={Ticket} tone="red" title="Ticket di assistenza" desc="Chi ha bisogno preme un bottone e si apre un canale privato con lo staff."
          tip="Dopo aver salvato premi «Invia il pannello»: pubblica nel canale scelto il messaggio con il bottone «Apri ticket»." {...card("tickets")}>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Categoria dove creare i ticket"><ChannelSelect value={f.tickets.categoryId} onChange={(v) => upd("tickets", { categoryId: v })} types={["category"]} placeholder="Seleziona una categoria" /></Field>
            <Field label="Ruoli dello staff che vedono i ticket"><MultiCheck options={roleOpts} value={f.tickets.staffRoleIds || []} onChange={(v) => upd("tickets", { staffRoleIds: v })} /></Field>
          </div>
          <Field label="Messaggio mostrato all'apertura"><MessageMini value={f.tickets.openMessage} onChange={(openMessage) => upd("tickets", { openMessage })} /></Field>
          <Button variant="outline" onClick={() => setPanelOpen(true)} data-testid="create-ticket-panel-btn" className={outlineBtn}><Ticket className="mr-2 h-4 w-4" />Invia il pannello</Button>
        </FeatureCard>
      </Section>

      <Section title="Programmati">
        <FeatureCard icon={CalendarClock} tone="green" title="Messaggi programmati" desc="Il bot invia un messaggio a orari fissi, ogni giorno, settimana o mese."
          enabled={sched.enabled ?? true} onToggle={(v) => upd("scheduled", { enabled: v })} onSave={() => save("scheduled")} saving={saving === "scheduled"} testId="automation-scheduled"
          tip="Esempi: il promemoria delle regole ogni lunedì, «buongiorno» ogni mattina, la classifica livelli ogni fine mese.">
          <div className="rounded-xl border border-[#232838] bg-[#131620] p-3">
            <p className={"mb-2 text-xs font-bold uppercase tracking-wide text-[#B5BAC1]"}>Nuovo invio</p>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={schedFreq.freq} onValueChange={(v) => setSchedFreq({ ...schedFreq, freq: v })}>
                <SelectTrigger className={`${inputC} w-44`}><SelectValue /></SelectTrigger>
                <SelectContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
                  <SelectItem value="daily" className="focus:bg-[#252B3D]">Ogni giorno</SelectItem>
                  <SelectItem value="weekly" className="focus:bg-[#252B3D]">Ogni settimana</SelectItem>
                  <SelectItem value="monthly" className="focus:bg-[#252B3D]">Ogni mese</SelectItem>
                  <SelectItem value="interval" className="focus:bg-[#252B3D]">Ogni tot minuti</SelectItem>
                </SelectContent>
              </Select>
              {schedFreq.freq === "weekly" && (
                <Select value={String(schedFreq.dow)} onValueChange={(v) => setSchedFreq({ ...schedFreq, dow: Number(v) })}>
                  <SelectTrigger className={`${inputC} w-36`}><SelectValue /></SelectTrigger>
                  <SelectContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
                    {DAYS.map((d, i) => <SelectItem key={i} value={String(i)} className="capitalize focus:bg-[#252B3D]">{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {schedFreq.freq === "monthly" && <span className="flex items-center gap-2 text-sm text-[#B5BAC1]">giorno <Input type="number" min={1} max={28} value={schedFreq.dom} onChange={(e) => setSchedFreq({ ...schedFreq, dom: Number(e.target.value) })} className={`${inputC} w-20`} /></span>}
              {schedFreq.freq !== "interval" && <span className="flex items-center gap-2 text-sm text-[#B5BAC1]">alle <Input type="time" value={schedFreq.time} onChange={(e) => setSchedFreq({ ...schedFreq, time: e.target.value })} className={`${inputC} w-28`} /></span>}
              {schedFreq.freq === "interval" && <span className="flex items-center gap-2 text-sm text-[#B5BAC1]">ogni <Input type="number" min={5} max={59} value={schedFreq.every} onChange={(e) => setSchedFreq({ ...schedFreq, every: Number(e.target.value) })} className={`${inputC} w-20`} /> minuti</span>}
              <Button size="sm" onClick={addSched} className={primaryBtn} data-testid="add-scheduled-btn"><Plus className="mr-1 h-4 w-4" />Aggiungi</Button>
            </div>
          </div>
          {(sched.items || []).map((it) => (
            <div key={it.id} className="space-y-3 rounded-xl border border-[#232838] bg-[#131620] p-3">
              <div className="flex items-center gap-2">
                <CalendarClock className="h-4 w-4 text-[#10B981]" />
                <span className="text-sm font-semibold capitalize text-white">{describeCron(it.cron)}</span>
                <Switch checked={it.enabled} onCheckedChange={(enabled) => updSched(it.id, { enabled })} className="ml-auto" />
                <Button size="icon" variant="ghost" onClick={() => upd("scheduled", { items: sched.items.filter((x) => x.id !== it.id) })} className="text-[#F23F43] hover:bg-[#F23F43]/10"><Trash2 className="h-4 w-4" /></Button>
              </div>
              <Field label="Canale"><ChannelSelect value={it.channelId} onChange={(v) => updSched(it.id, { channelId: v })} /></Field>
              <MessageMini value={it.message} onChange={(message) => updSched(it.id, { message })} />
            </div>
          ))}
          {(sched.items || []).length === 0 && <p className="text-sm text-[#949BA4]">Nessun messaggio programmato: scegli quando e premi «Aggiungi».</p>}
        </FeatureCard>
      </Section>

      <Dialog open={panelOpen} onOpenChange={setPanelOpen}>
        <DialogContent className="border-[#232838] bg-[#161923] text-[#DBDEE1]">
          <DialogHeader><DialogTitle className="text-white">Invia il pannello dei ticket</DialogTitle></DialogHeader>
          <p className="text-sm text-[#949BA4]">Pubblica un messaggio con il bottone «Apri ticket» nel canale che scegli (di solito #assistenza).</p>
          <ChannelSelect value={panelChannel} onChange={setPanelChannel} types={["text", "announcement"]} testId="panel-channel-select" />
          <DialogFooter><Button onClick={createPanel} className={primaryBtn} data-testid="panel-send-btn"><Send className="mr-2 h-4 w-4" />Invia pannello</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
